import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Hash, Music as MusicIcon, User, Search } from 'lucide-react';
import clsx from 'clsx';
import { hashtagsApi } from '@/api/hashtags';
import { musicApi } from '@/api/music';
import { apiClient } from '@/api/client';
import Avatar from '@/components/ui/Avatar';

type Tab = 'all' | 'users' | 'tags' | 'sounds';

/**
 * TikTok Discover — единый поиск по users / #hashtags / музыке.
 *   Пусто    → trending hashtags + popular sounds.
 *   С q      → параллельные запросы ко всем трём API, рендерим секции.
 */
export default function DiscoverPage({
  onClose,
  onOpenTag,
  onOpenSound,
  onOpenUser,
}: {
  onClose: () => void;
  onOpenTag: (tag: string) => void;
  onOpenSound: (trackId: string) => void;
  onOpenUser?: (userId: string) => void;
}) {
  const [q, setQ] = useState('');
  const [tab, setTab] = useState<Tab>('all');
  const qTrim = q.trim();

  // Users via global search
  const { data: users } = useQuery({
    queryKey: ['discover', 'users', qTrim],
    queryFn: async () => {
      if (!qTrim) return [] as any[];
      const r = await apiClient.get('/api/search/global', { params: { q: qTrim, limit: 15 } });
      return (r.data?.users ?? []) as Array<{ id: string; username: string; displayName: string; avatarUrl: string | null }>;
    },
    enabled: qTrim.length > 0,
  });

  const { data: tags } = useQuery({
    queryKey: ['discover', 'tags', qTrim],
    queryFn: () => qTrim ? hashtagsApi.search(qTrim, 20) : hashtagsApi.trending(20),
  });

  const { data: sounds } = useQuery({
    queryKey: ['discover', 'sounds', qTrim],
    queryFn: () => musicApi.list({ q: qTrim || undefined, sort: 'popular', limit: 20 }),
  });

  const showUsers = tab === 'all' || tab === 'users';
  const showTags = tab === 'all' || tab === 'tags';
  const showSounds = tab === 'all' || tab === 'sounds';

  return (
    <div className="fixed inset-0 z-[60] bg-surface-950 text-white flex flex-col">
      <div className="flex items-center gap-3 p-3 border-b border-surface-800">
        <button onClick={onClose}><ArrowLeft size={20} /></button>
        <div className="flex-1 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-500" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Пользователи, #теги, звуки"
            className="w-full bg-surface-900 rounded-lg pl-9 pr-3 py-2 text-sm outline-none"
            autoFocus
          />
        </div>
      </div>

      <div className="flex gap-2 px-3 py-2 border-b border-surface-800 text-xs">
        {([
          ['all', 'Всё'],
          ['users', 'Юзеры'],
          ['tags', 'Теги'],
          ['sounds', 'Звуки'],
        ] as const).map(([k, label]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={clsx(
              'px-3 py-1 rounded-full',
              tab === k ? 'bg-brand-500 text-white' : 'bg-surface-800 text-surface-400',
            )}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto">
        {showUsers && (users?.length ?? 0) > 0 && (
          <Section title="Пользователи" icon={<User size={14} />}>
            {users!.map((u) => (
              <button
                key={u.id}
                onClick={() => onOpenUser?.(u.id)}
                className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-surface-900 text-left"
              >
                <Avatar src={u.avatarUrl} username={u.username} size="md" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{u.displayName}</div>
                  <div className="text-xs text-surface-400 truncate">@{u.username}</div>
                </div>
              </button>
            ))}
          </Section>
        )}

        {showTags && (tags?.length ?? 0) > 0 && (
          <Section title={qTrim ? 'Теги' : 'Тренды'} icon={<Hash size={14} />}>
            {tags!.map((t) => (
              <button
                key={t.id}
                onClick={() => onOpenTag(t.tag)}
                className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-surface-900 text-left"
              >
                <div className="w-9 h-9 rounded-full bg-surface-800 flex items-center justify-center">
                  <Hash size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">#{t.tag}</div>
                  <div className="text-xs text-surface-400">
                    {(t as any).count ?? t.storyCount} сторис
                  </div>
                </div>
              </button>
            ))}
          </Section>
        )}

        {showSounds && (sounds?.items.length ?? 0) > 0 && (
          <Section title={qTrim ? 'Звуки' : 'Популярные звуки'} icon={<MusicIcon size={14} />}>
            {sounds!.items.map((t) => (
              <button
                key={t.id}
                onClick={() => onOpenSound(t.id)}
                className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-surface-900 text-left"
              >
                <div className="w-9 h-9 rounded-lg bg-surface-800 flex items-center justify-center overflow-hidden">
                  {t.coverUrl ? (
                    <img src={t.coverUrl} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <MusicIcon size={14} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{t.title}</div>
                  <div className="text-xs text-surface-400 truncate">
                    {t.artist} · {t.usageCount} сторис
                  </div>
                </div>
              </button>
            ))}
          </Section>
        )}

        {qTrim && !users?.length && !tags?.length && !sounds?.items.length && (
          <div className="p-8 text-center text-surface-500 text-sm">Ничего не найдено</div>
        )}
      </div>
    </div>
  );
}

function Section({
  title, icon, children,
}: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="py-1">
      <div className="flex items-center gap-1.5 px-4 py-2 text-xs text-surface-400 uppercase tracking-wider">
        {icon}{title}
      </div>
      {children}
    </div>
  );
}
